import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  Shield,
  Award,
  LogOut,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { BookingModal } from '../components/BookingModal';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { Modal } from '../components/Modal';
import { ProviderReviewsSection } from '../components/ProviderReviewsSection';
import { useAuth } from '../context/AuthContext';
import { getProviderProfile, createBooking } from '../services/api';
import { ProviderProfile, ReviewsSummary } from '../types';
import { ROUTES } from '../constants';
import { trackViewContent, trackProviderViewed } from '../utils/analytics';

export const ProviderDetailPage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [provider, setProvider] = useState<ProviderProfile | null>(null);
  const [reviewsSummary, setReviewsSummary] = useState<ReviewsSummary | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);

  useEffect(() => {
    if (user?.role === 'user' && !user.isProfileCompleted) {
      navigate(ROUTES.HOME_PAGE, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchProviderDetails = async () => {
      if (!providerId) {
        setError('Provider ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getProviderProfile(providerId);

        if (response.success && response.provider) {
          if (response.provider.isBlocked) {
            setError('This provider is not available');
            setTimeout(() => {
              navigate(ROUTES.FIND_PROVIDERS);
            }, 3000);
            return;
          }

          setProvider(response.provider);
          setReviewsSummary(response.reviewsSummary);

          const typeOfCareStr = Array.isArray(response.provider.typeOfCare)
            ? response.provider.typeOfCare.join(', ')
            : response.provider.typeOfCare || 'Care Provider';

          trackViewContent(
            response.provider.name || 'Provider Profile',
            providerId,
            {
              content_category: typeOfCareStr,
            }
          );
          trackProviderViewed({
            provider_id: providerId,
            provider_name: response.provider.name,
            type_of_care: typeOfCareStr,
            location: response.provider.location,
          });
        } else {
          setError(response.message || 'Failed to load provider details');
          setTimeout(() => {
            navigate(ROUTES.FIND_PROVIDERS);
          }, 3000);
        }
      } catch (err) {
        setError('Failed to load provider details');
      } finally {
        setLoading(false);
      }
    };

    fetchProviderDetails();
  }, [providerId]);

  const handleBookingSubmit = async (bookingData: any) => {
    try {
      const response = await createBooking(bookingData);

      if (response.success) {
        setBookingDetails(response.booking);
        setShowBookingModal(false);
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate(ROUTES.MY_BOOKINGS);
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to create booking');
      }
    } catch (err) {
      throw err;
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(ROUTES.MY_BOOKINGS);
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const getDayAvailability = (day: keyof ProviderProfile['availability']) => {
    if (!provider?.availability) return 'Not available';
    return provider.availability[day] || 'Not available';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Logo size="md" />
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Provider Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The provider you are looking for does not exist.'}</p>
            <Button onClick={() => navigate(ROUTES.FIND_PROVIDERS)}>
              Back to Search
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo size="md" />
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="relative h-64 bg-gradient-to-r from-blue-500 to-blue-600">
                {provider.gallery && provider.gallery.length > 0 && (
                  <ImageWithFallback
                    src={provider.gallery[0]}
                    alt={provider.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-3xl font-bold text-gray-900">{provider.name}</h1>
                      {provider.isVerified && (
                        <Badge variant="success" className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-gray-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{provider.location}</span>
                      </div>
                      {provider.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          <span>{provider.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {Array.isArray(provider.typeOfCare) ? (
                      provider.typeOfCare.map((type, index) => (
                        <Badge key={index} variant="primary">{type}</Badge>
                      ))
                    ) : (
                      <Badge variant="primary">{provider.typeOfCare}</Badge>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">Service radius: {provider.serviceRadiusKm} km</span>
                </div>

                <Button
                  onClick={() => setShowBookingModal(true)}
                  className="w-full"
                  size="lg"
                >
                  Book Now
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Services Offered</h2>
              <div className="flex flex-wrap gap-2">
                {provider.services.map((service, index) => (
                  <Badge key={index} variant="info">
                    {service}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-600" />
                Credentials & Qualifications
              </h2>
              <ul className="space-y-2">
                {provider.credentials.map((credential, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{credential}</span>
                  </li>
                ))}
              </ul>
            </div>

            {provider.gallery && provider.gallery.length > 1 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {provider.gallery.map((image, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                      <ImageWithFallback
                        src={image}
                        alt={`${provider.name} - Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {user?.role === 'user' && (
              <ProviderReviewsSection
                providerId={Number(providerId)}
                providerName={provider.name}
                initialSummary={reviewsSummary}
              />
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Pricing</h2>
              <div className="space-y-3">
                {provider.pricing.hourly && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Hourly Rate</span>
                    <span className="text-2xl font-bold text-gray-900">£{provider.pricing.hourly}</span>
                  </div>
                )}
                {provider.pricing.daily && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Daily Rate</span>
                    <span className="text-xl font-bold text-gray-900">£{provider.pricing.daily}</span>
                  </div>
                )}
                {provider.pricing.monthly && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Monthly Rate</span>
                    <span className="text-xl font-bold text-gray-900">£{provider.pricing.monthly}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Availability
              </h2>
              <div className="space-y-2 text-sm">
                {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => (
                  <div key={day} className="flex justify-between">
                    <span className="text-gray-600 capitalize">{day}</span>
                    <span className="font-medium text-gray-900">{getDayAvailability(day)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <div className="space-y-3">
                {provider.email && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">{provider.email}</span>
                  </div>
                )}
                {provider.phone && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">{provider.phone}</span>
                  </div>
                )}
                {provider.address && (
                  <div className="flex items-start gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">
                      {provider.address}, {provider.city} {provider.postalCode}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        providerId={Number(providerId)}
        providerName={provider.name}
        onSubmit={handleBookingSubmit}
      />

      <Modal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Booking Successful!"
        variant="success"
        message={`Your booking with ${provider.name} has been confirmed. ${bookingDetails?.appointmentDate ? `Appointment scheduled for ${new Date(bookingDetails.appointmentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.` : ''} You will be redirected to your bookings page.`}
        actionLabel="View My Bookings"
        onAction={handleSuccessModalClose}
      />
    </div>
  );
};
